<!--begin::Card-->
<div class="card">
  <!--begin::Card header-->
  <div class="card-header border-0 pt-6">
    <!--begin::Card title-->
    <div class="card-title">
      <h2>{{ isEditMode ? 'Edit Shipping Schedule' : 'Add Shipping Schedule' }}</h2>
    </div>
    <!--end::Card title-->
  </div>
  <!--end::Card header-->

  <!--begin::Card body-->
  <div class="card-body pt-0">
    <!-- Loading indicator for edit mode data -->
    <div *ngIf="isLoadingData" class="text-center py-10">
      <span class="spinner-border spinner-border-lg text-primary"></span>
      <div class="mt-3">Loading shipping schedule data...</div>
    </div>

    <form *ngIf="!isLoadingData" [formGroup]="shippingScheduleForm" (ngSubmit)="onSubmit()">
      <div class="row">
        <!-- Region -->
        <div class="col-md-6 mb-5">
          <label class="form-label required">Region</label>
          <select class="form-select form-select-solid" formControlName="region">
            <option value="">Select Region</option>
            <option *ngFor="let region of regions" [value]="region.value">
              {{ region.label }}
            </option>
          </select>
          <div *ngIf="shippingScheduleForm.get('region')?.invalid && shippingScheduleForm.get('region')?.touched"
               class="text-danger mt-1">
            Region is required
          </div>
        </div>

        <!-- Shipping Company -->
        <div class="col-md-6 mb-5">
          <label class="form-label required">Shipping Company</label>
          <div class="position-relative">
            <input
              type="text"
              class="form-control form-control-solid"
              [(ngModel)]="shippingCompanySearchTerm"
              [ngModelOptions]="{standalone: true}"
              (input)="onShippingCompanySearchInput($event)"
              (focus)="onShippingCompanyInputFocus()"
              (blur)="onShippingCompanyInputBlur()"
              placeholder="Search shipping companies..."
              autocomplete="off"
            />

            <!-- Loading indicator -->
            <div *ngIf="isShippingCompanySearching" class="position-absolute top-50 end-0 translate-middle-y me-3">
              <span class="spinner-border spinner-border-sm text-primary"></span>
            </div>

            <!-- Search results dropdown -->
            <div *ngIf="showShippingCompanyDropdown && shippingCompanySearchResults.length > 0"
                 class="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                 style="z-index: 1000; max-height: 200px; overflow-y: auto;">
              <div *ngFor="let company of shippingCompanySearchResults"
                   class="p-3 border-bottom cursor-pointer hover-bg-light"
                   (click)="selectShippingCompany(company)">
                <div class="fw-bold">{{ company.company_name || 'Unknown Company' }}</div>
                <div class="text-muted fs-8" *ngIf="company.address">
                  {{ company.address }}
                </div>
              </div>
            </div>
          </div>
          <div *ngIf="shippingScheduleForm.get('shipping_company_id')?.invalid && shippingScheduleForm.get('shipping_company_id')?.touched"
               class="text-danger mt-1">
            Shipping Company is required
          </div>
        </div>

        <!-- Vessel -->
        <div class="col-md-6 mb-5">
          <label class="form-label required">Vessel</label>
          <input type="text" class="form-control form-control-solid" formControlName="vessel" placeholder="Enter vessel name">
          <div *ngIf="shippingScheduleForm.get('vessel')?.invalid && shippingScheduleForm.get('vessel')?.touched"
               class="text-danger mt-1">
            Vessel is required
          </div>
        </div>

        <!-- Type -->
        <div class="col-md-6 mb-5">
          <label class="form-label required">Type</label>
          <div class="d-flex gap-5 mt-3">
            <div class="form-check">
              <input class="form-check-input" type="radio" formControlName="type" value="roro" id="type_roro">
              <label class="form-check-label" for="type_roro">
                Roro
              </label>
            </div>
            <div class="form-check">
              <input class="form-check-input" type="radio" formControlName="type" value="container" id="type_container">
              <label class="form-check-label" for="type_container">
                Container
              </label>
            </div>
          </div>
          <div *ngIf="shippingScheduleForm.get('type')?.invalid && shippingScheduleForm.get('type')?.touched"
               class="text-danger mt-1">
            Type is required
          </div>
        </div>

        <!-- Voyage No -->
        <div class="col-md-6 mb-5">
          <label class="form-label required">Voyage No</label>
          <input type="text" class="form-control form-control-solid" formControlName="voyage_no" placeholder="Enter voyage number">
          <div *ngIf="shippingScheduleForm.get('voyage_no')?.invalid && shippingScheduleForm.get('voyage_no')?.touched"
               class="text-danger mt-1">
            Voyage No is required
          </div>
        </div>
      </div>

      <!-- Port Listings -->
      <div class="row mt-10" *ngIf="!isLoadingPorts">
        <!-- From Ports -->
        <div class="col-md-6">
          <h4 class="mb-5">Port of Shipment</h4>
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Port Name</th>
                  <th>Input</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let port of portFromList">
                  <td class="fw-bold">{{ port.name }}</td>
                  <td>
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      [(ngModel)]="port.inputValue"
                      [ngModelOptions]="{standalone: true}"
                      (input)="onPortFromInputChange(port, $event)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Destination Ports -->
        <div class="col-md-6">
          <h4 class="mb-5">Port of Discharge</h4>
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead class="table-light">
                <tr>
                  <th>Port Name</th>
                  <th>Input</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let port of destinationPortList">
                  <td class="fw-bold">{{ port.name }}</td>
                  <td>
                    <input
                      type="date"
                      class="form-control form-control-sm"
                      [(ngModel)]="port.inputValue"
                      [ngModelOptions]="{standalone: true}"
                      (input)="onDestinationPortInputChange(port, $event)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Loading indicator for ports -->
      <div *ngIf="isLoadingPorts" class="text-center py-10">
        <span class="spinner-border spinner-border-lg text-primary"></span>
        <div class="mt-3">Loading port data...</div>
      </div>

      <!-- Form Actions -->
      <div class="d-flex justify-content-end gap-3 mt-10">
        <button type="button" class="btn btn-light" (click)="onCancel()" [disabled]="isSubmitting || isLoadingData">
          Cancel
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="isSubmitting || isLoadingData">
          <span *ngIf="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
          {{ isSubmitting ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Shipping Schedule' : 'Create Shipping Schedule') }}
        </button>
      </div>
    </form>
  </div>
  <!--end::Card body-->
</div>
<!--end::Card-->
